import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../App';
import { useTenant } from '../context/TenantContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, ChevronUp, ChevronDown, List, Grid, GripVertical, Upload, Download, Lock } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { formatCurrency } from '../utils/currency';
import { getTranslation, setTranslation, validateTranslations } from '../utils/translations';
import { getErrorMessage } from '../utils/errors';
import ImportFileDialog from '../components/ImportFileDialog';

// Language display names
const LANGUAGE_NAMES = {
  en: 'English',
  tr: 'Türkçe',
  de: 'Deutsch',
  fr: 'Français',
  es: 'Español',
  ar: 'العربية',
  ru: 'Русский',
};

const MenuManagement = () => {
  const { t } = useTranslation();
  const { tenant } = useTenant();
  const { token, API } = useContext(AuthContext);
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [modifiers, setModifiers] = useState([]); // Legacy modifiers (deprecated)
  const [modifierGroups, setModifierGroups] = useState([]); // New: modifier groups with options
  const [ingredients, setIngredients] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentForm, setCurrentForm] = useState('category');
  const [formData, setFormData] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterAvailability, setFilterAvailability] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [viewMode, setViewMode] = useState(() => {
    const saved = localStorage.getItem('menu_view_mode');
    return saved === 'list' || saved === 'grid' ? saved : 'grid';
  });

  // Persist view mode to localStorage
  useEffect(() => {
    localStorage.setItem('menu_view_mode', viewMode);
  }, [viewMode]);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  // Import/Export state
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importType, setImportType] = useState('categories');
  const [hasImportExport, setHasImportExport] = useState(false);
  const [exportLoading, setExportLoading] = useState(null); // 'categories' | 'menu-items' | null

  // Check if tenant has import_export add-on
  useEffect(() => {
    if (token) {
      axios.get(`${API}/addons/check/import_export`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(res => setHasImportExport(res.data?.active === true))
        .catch(() => setHasImportExport(false));
    }
  }, [token, API]);

  // Export handler
  const handleExport = async (type) => {
    if (!hasImportExport) {
      toast.error(t('menu.importExport.addonRequired', 'Import/Export add-on required'));
      return;
    }
    setExportLoading(type);
    try {
      const response = await axios.get(`${API}/export/${type}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${type}_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success(t('menu.importExport.exportSuccess', 'Export downloaded successfully'));
    } catch (error) {
      const msg = error.response?.data?.detail || error.message;
      if (error.response?.status === 403) {
        toast.error(t('menu.importExport.addonRequired', 'Import/Export add-on required'));
      } else {
        toast.error(t('menu.importExport.exportError', 'Export failed: ') + msg);
      }
    } finally {
      setExportLoading(null);
    }
  };

  // Open import dialog
  const openImportDialog = (type) => {
    if (!hasImportExport) {
      toast.error(t('menu.importExport.addonRequired', 'Import/Export add-on required'));
      return;
    }
    setImportType(type);
    setImportDialogOpen(true);
  };

  useEffect(() => {
    // Only fetch data when token is available
    if (token) {
      fetchData();
    }
  }, [token]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [catRes, itemsRes, modGroupsRes, ingRes] = await Promise.all([
        axios.get(`${API}/categories`, { headers }),
        axios.get(`${API}/menu-items`, { headers }),
        axios.get(`${API}/modifier-groups`, { headers }),
        axios.get(`${API}/ingredients`, { headers })
      ]);
      setCategories(catRes.data);
      setMenuItems(itemsRes.data);
      setModifierGroups(modGroupsRes.data);
      setIngredients(ingRes.data);
    } catch (error) {
      toast.error(t('messages.error'));
    }
  };

  const handleSubmit = async (e, type) => {
    e.preventDefault();
    
    // Validate translations
    if (!validateTranslations(formData.translations, 'name')) {
      toast.error(t('messages.provideTranslation'));
      return;
    }
    
    try {
      const endpoints = {
        category: '/categories',
        modifier: '/modifier-groups',  // Updated to use modifier groups
        item: '/menu-items'
      };
      const endpoint = endpoints[type];
      
      if (editMode) {
        // Update existing item
        await axios.put(`${API}${endpoint}/${editId}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        // Create new item
        await axios.post(`${API}${endpoint}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      toast.success(t('messages.success'));
      setDialogOpen(false);
      setFormData({});
      setEditMode(false);
      setEditId(null);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, t('messages.error')));
    }
  };

  const handleDelete = (id, type, item) => {
    setItemToDelete({ id, type, item });
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    try {
      const { id, type } = itemToDelete;
      const endpoints = {
        category: `/categories/${id}`,
        modifier: `/modifier-groups/${id}`,  // Updated to use modifier groups
        item: `/menu-items/${id}`
      };
      const endpoint = endpoints[type];
      await axios.delete(`${API}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('messages.success'));
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, t('messages.error')));
    }
  };

  // Category drag and drop reordering
  const handleCategoryDragEnd = (result) => {
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;
    
    const parentCategories = categories.filter(c => !c.parent_category_id);
    const reordered = Array.from(parentCategories);
    const [movedItem] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, movedItem);
    
    // Rebuild categories array with subcategories in place
    const newCategories = [];
    reordered.forEach(parent => {
      newCategories.push(parent);
      const subs = categories.filter(c => c.parent_category_id === parent.id);
      newCategories.push(...subs);
    });
    
    setCategories(newCategories);
    saveCategoryOrder(newCategories);
  };

  const saveCategoryOrder = async (orderedCategories) => {
    try {
      const category_ids = orderedCategories.map(cat => cat.id);
      await axios.put(`${API}/categories/reorder`, { category_ids }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('messages.categoryOrderUpdated'));
    } catch (error) {
      toast.error(t('messages.orderUpdateFailed'));
      fetchData(); // Reload if failed
    }
  };

  // Menu item drag and drop reordering
  const handleItemDragEnd = (result, filteredItems) => {
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;
    
    // Reorder the filtered items
    const reordered = Array.from(filteredItems);
    const [movedItem] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, movedItem);
    
    // Update local state with new order values
    const newItems = [...menuItems];
    reordered.forEach((item, idx) => {
      const itemIdx = newItems.findIndex(i => i.id === item.id);
      if (itemIdx !== -1) {
        newItems[itemIdx].order = idx;
      }
    });
    setMenuItems(newItems);
    
    // Save to backend
    saveMenuItemOrder(reordered);
  };

  const saveMenuItemOrder = async (orderedItems) => {
    try {
      const item_ids = orderedItems.map(item => item.id);
      await axios.put(`${API}/menu-items/reorder`, { item_ids }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('messages.itemOrderUpdated'));
    } catch (error) {
      toast.error(t('messages.orderUpdateFailed'));
      fetchData(); // Reload if failed
    }
  };

  const openDialog = (type, item = null) => {
    setCurrentForm(type);
    if (item) {
      setEditMode(true);
      setEditId(item.id);
      // Ensure translations object exists
      const itemData = { ...item };
      if (!itemData.translations) {
        itemData.translations = {};
      }
      setFormData(itemData);
    } else {
      setEditMode(false);
      setEditId(null);
      setFormData({ translations: {} }); // Initialize with empty translations
    }
    setDialogOpen(true);
  };

  // Helper to update translation field
  const updateTranslation = (lang, field, value) => {
    const updatedTranslations = setTranslation(formData.translations || {}, lang, field, value);
    setFormData({ ...formData, translations: updatedTranslations });
  };

  return (
    <div className="menu-management">
      <Tabs defaultValue="categories" className="w-full">
        <div className="tabs-header-row">
          <TabsList className="mb-0">
            <TabsTrigger value="categories" data-testid="categories-tab">{t('menu.categories')}</TabsTrigger>
            <TabsTrigger value="items" data-testid="items-tab">{t('menu.menuItems')}</TabsTrigger>
            <TabsTrigger value="modifiers" data-testid="modifiers-tab">{t('menu.modifiers')}</TabsTrigger>
          </TabsList>
          <div className="view-toggle">
            <button
              onClick={() => setViewMode('list')}
              className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
              title="List view"
            >
              <List size={18} />
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`view-toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
              title="Grid view"
            >
              <Grid size={18} />
            </button>
          </div>
        </div>

        <TabsContent value="categories">
          <div className="section-header">
            <h2 className="section-title">{t('menu.categories')}</h2>
            <div className="section-header-actions">
              {hasImportExport ? (
                <>
                  <Button variant="outline" size="sm" onClick={() => openImportDialog('categories')} title={t('menu.importExport.importCategories', 'Import Categories')}>
                    <Upload size={16} /> {t('menu.importExport.import', 'Import')}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleExport('categories')} disabled={exportLoading === 'categories'} title={t('menu.importExport.exportCategories', 'Export Categories')}>
                    {exportLoading === 'categories' ? <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" /> : <Download size={16} />} {t('menu.importExport.export', 'Export')}
                  </Button>
                </>
              ) : (
                <Button variant="outline" size="sm" className="import-export-locked" onClick={() => window.location.href = '/admin/addons'} title={t('menu.importExport.addonRequired', 'Import/Export add-on required')}>
                  <Lock size={14} /> {t('menu.importExport.importExport', 'Import/Export')}
                </Button>
              )}
              <Button onClick={() => openDialog('category')} data-testid="add-category-btn">
                <Plus size={16} /> {t('menu.addCategory')}
              </Button>
            </div>
          </div>

          <div className="card mt-4">
            {(() => {
              const primaryLang = tenant?.supportedLanguages?.[0] || 'tr';
              const parentCategories = categories.filter(c => !c.parent_category_id);
              
              if (viewMode === 'grid') {
                return (
                  <DragDropContext onDragEnd={handleCategoryDragEnd}>
                    <Droppable droppableId="categories-grid">
                      {(droppableProvided) => (
                        <div 
                          ref={droppableProvided.innerRef} 
                          {...droppableProvided.droppableProps}
                          className="items-grid"
                        >
                          {parentCategories.map((cat, index) => {
                            const subcategories = categories.filter(c => c.parent_category_id === cat.id);
                            return (
                              <Draggable key={cat.id} draggableId={cat.id} index={index}>
                                {(provided, snapshot) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    className={`category-card ${snapshot.isDragging ? 'dragging' : ''}`}
                                    data-testid={`category-card-${cat.id}`}
                                  >
                                    <div className="drag-handle-card" {...provided.dragHandleProps}>
                                      <GripVertical size={18} />
                                    </div>
                                    <div className="category-card-content">
                                      <div className="category-card-header">
                                        <h3 className="category-card-title">
                                          {cat.is_parent && '📁 '}
                                          {getTranslation(cat.translations, primaryLang, 'name')}
                                        </h3>
                                      </div>
                                      {subcategories.length > 0 && (
                                        <p className="category-card-subs">
                                          {subcategories.length} {t('dashboard.subcategories')}
                                        </p>
                                      )}
                                      <div className="category-card-actions">
                                        <button onClick={() => openDialog('category', cat)} className="edit-btn">
                                          <Edit2 size={16} />
                                        </button>
                                        <button onClick={() => handleDelete(cat.id, 'category', cat)} className="delete-btn">
                                          <Trash2 size={16} />
                                        </button>
                                      </div>
                                      {subcategories.length > 0 && (
                                        <div className="category-subcategories">
                                          {subcategories.map(sub => (
                                            <div key={sub.id} className="subcategory-chip">
                                              <span>↳ {getTranslation(sub.translations, primaryLang, 'name')}</span>
                                              <div className="subcategory-actions">
                                                <button onClick={() => openDialog('category', sub)} className="edit-btn-sm">
                                                  <Edit2 size={12} />
                                                </button>
                                                <button onClick={() => handleDelete(sub.id, 'category', sub)} className="delete-btn-sm">
                                                  <Trash2 size={12} />
                                                </button>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </Draggable>
                            );
                          })}
                          {droppableProvided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                );
              }
              
              // List view
              return (
                <DragDropContext onDragEnd={handleCategoryDragEnd}>
                  <Droppable droppableId="categories-list">
                    {(droppableProvided) => (
                      <table>
                        <thead>
                          <tr>
                            <th style={{ width: '50px' }}></th>
                            {(tenant?.supportedLanguages || ['tr']).map(lang => (
                              <th key={`header-${lang}`}>
                                {t('menu.categoryName')}
                                {(tenant?.supportedLanguages || ['tr']).length > 1 && ` (${LANGUAGE_NAMES[lang] || lang.toUpperCase()})`}
                              </th>
                            ))}
                            <th>{t('menu.parentCategory')}</th>
                            <th>{t('common.actions')}</th>
                          </tr>
                        </thead>
                        <tbody ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
                          {parentCategories.map((parentCat, index) => {
                            const subcategories = categories.filter(c => c.parent_category_id === parentCat.id);
                            const supportedLangs = tenant?.supportedLanguages || ['tr'];
                            return (
                              <React.Fragment key={parentCat.id}>
                                <Draggable draggableId={parentCat.id} index={index}>
                                  {(provided, snapshot) => (
                                    <tr 
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      data-testid={`category-row-${parentCat.id}`} 
                                      className={`${parentCat.is_parent ? 'parent-category-row' : ''} ${snapshot.isDragging ? 'dragging-row' : ''}`}
                                    >
                                      <td>
                                        <div className="drag-handle" {...provided.dragHandleProps}>
                                          <GripVertical size={18} />
                                        </div>
                                      </td>
                                      {supportedLangs.map((lang, idx) => (
                                        <td key={`cat-${parentCat.id}-${lang}`} data-testid={`category-name-${lang}`}>
                                          {idx === 0 && parentCat.is_parent && <strong>📁 </strong>}
                                          {getTranslation(parentCat.translations, lang, 'name')}
                                        </td>
                                      ))}
                                      <td>—</td>
                                      <td>
                                        <div className="flex gap-2">
                                          <button onClick={() => openDialog('category', parentCat)} className="edit-btn" data-testid="edit-category-btn">
                                            <Edit2 size={16} />
                                          </button>
                                          <button onClick={() => handleDelete(parentCat.id, 'category', parentCat)} className="delete-btn" data-testid="delete-category-btn">
                                            <Trash2 size={16} />
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                </Draggable>
                                {subcategories.map((subCat) => (
                                  <tr key={subCat.id} data-testid={`category-row-${subCat.id}`} className="subcategory-row">
                                    <td></td>
                                    {supportedLangs.map((lang, idx) => (
                                      <td key={`subcat-${subCat.id}-${lang}`} data-testid={`category-name-${lang}`} className={idx === 0 ? "pl-6" : ""}>
                                        {idx === 0 && "↳ "}{getTranslation(subCat.translations, lang, 'name')}
                                      </td>
                                    ))}
                                    <td className="text-gray-500 text-sm">{getTranslation(parentCat.translations, supportedLangs[0], 'name')}</td>
                                    <td>
                                      <div className="flex gap-2">
                                        <button onClick={() => openDialog('category', subCat)} className="edit-btn" data-testid="edit-category-btn">
                                          <Edit2 size={16} />
                                        </button>
                                        <button onClick={() => handleDelete(subCat.id, 'category', subCat)} className="delete-btn" data-testid="delete-category-btn">
                                          <Trash2 size={16} />
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </React.Fragment>
                            );
                          })}
                          {droppableProvided.placeholder}
                        </tbody>
                      </table>
                    )}
                  </Droppable>
                </DragDropContext>
              );
            })()}
          </div>
        </TabsContent>

        <TabsContent value="items">
          <div className="section-header">
            <h2 className="section-title">{t('menu.menuItems')}</h2>
            <div className="section-header-actions">
              {hasImportExport ? (
                <>
                  <Button variant="outline" size="sm" onClick={() => openImportDialog('menu_items')} title={t('menu.importExport.importItems', 'Import Menu Items')}>
                    <Upload size={16} /> {t('menu.importExport.import', 'Import')}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleExport('menu-items')} disabled={exportLoading === 'menu-items'} title={t('menu.importExport.exportItems', 'Export Menu Items')}>
                    {exportLoading === 'menu-items' ? <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" /> : <Download size={16} />} {t('menu.importExport.export', 'Export')}
                  </Button>
                </>
              ) : (
                <Button variant="outline" size="sm" className="import-export-locked" onClick={() => window.location.href = '/admin/addons'} title={t('menu.importExport.addonRequired', 'Import/Export add-on required')}>
                  <Lock size={14} /> {t('menu.importExport.importExport', 'Import/Export')}
                </Button>
              )}
              <Button onClick={() => openDialog('item')} data-testid="add-item-btn">
                <Plus size={16} /> {t('menu.addMenuItem')}
              </Button>
            </div>
          </div>

          <div className="filter-controls mt-4">
            <input
              type="text"
              placeholder={t('common.search') + '...'}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1); // Reset to first page on search
              }}
              className="filter-select"
              style={{ flex: '1 1 200px' }}
              data-testid="search-items"
            />
            <select
              value={filterCategory}
              onChange={(e) => {
                setFilterCategory(e.target.value);
                setCurrentPage(1);
              }}
              className="filter-select"
              data-testid="filter-category"
            >
              <option value="all">{t('menu.allCategories')}</option>
              {categories
                .filter(cat => !cat.parent_category_id) // Show parent categories as groups
                .map(parentCat => {
                  const subcategories = categories.filter(c => c.parent_category_id === parentCat.id);
                  const primaryLang = tenant?.supportedLanguages?.[0] || 'tr';
                  if (subcategories.length > 0) {
                    // Parent with subcategories - show as optgroup
                    return (
                      <optgroup key={parentCat.id} label={getTranslation(parentCat.translations, primaryLang, 'name')}>
                        {subcategories.map(sub => (
                          <option key={sub.id} value={sub.id}>
                            {getTranslation(sub.translations, primaryLang, 'name')}
                          </option>
                        ))}
                      </optgroup>
                    );
                  } else {
                    // Leaf category (no subcategories)
                    return (
                      <option key={parentCat.id} value={parentCat.id}>
                        {getTranslation(parentCat.translations, primaryLang, 'name')}
                      </option>
                    );
                  }
                })}
            </select>
            <select
              value={filterAvailability}
              onChange={(e) => {
                setFilterAvailability(e.target.value);
                setCurrentPage(1);
              }}
              className="filter-select"
              data-testid="filter-availability"
            >
              <option value="all">All Items</option>
              <option value="available">Available</option>
              <option value="unavailable">Unavailable</option>
            </select>
          </div>

          <div className="card mt-4">
            {(() => {
              const primaryLang = tenant?.supportedLanguages?.[0] || 'tr';
              const filteredItems = menuItems
                .filter(item => {
                  const categoryMatch = filterCategory === 'all' || item.category_id === filterCategory;
                  const availabilityMatch = 
                    filterAvailability === 'all' || 
                    (filterAvailability === 'available' && item.available !== false) ||
                    (filterAvailability === 'unavailable' && item.available === false);
                  const nameMatch = !searchTerm || 
                    getTranslation(item.translations, primaryLang, 'name')?.toLowerCase().includes(searchTerm.toLowerCase());
                  return categoryMatch && availabilityMatch && nameMatch;
                })
                .sort((a, b) => (a.order || 0) - (b.order || 0));
              
              const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
              const startIndex = (currentPage - 1) * itemsPerPage;
              const paginatedItems = filteredItems.slice(startIndex, startIndex + itemsPerPage);
              // Can only reorder when viewing a specific category (not all), no search, no availability filter
              const canReorder = filterCategory !== 'all' && !searchTerm && filterAvailability === 'all';
              
              if (viewMode === 'grid') {
                return (
                  <DragDropContext onDragEnd={(result) => canReorder && handleItemDragEnd(result, filteredItems)}>
                    <Droppable droppableId="items-grid">
                      {(droppableProvided) => (
                        <div 
                          ref={droppableProvided.innerRef} 
                          {...droppableProvided.droppableProps}
                          className="items-grid"
                        >
                          {paginatedItems.map((item, index) => (
                            <Draggable 
                              key={item.id} 
                              draggableId={item.id} 
                              index={index}
                              isDragDisabled={!canReorder}
                            >
                              {(provided, snapshot) => (
                                <div 
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  className={`item-card ${item.available === false ? 'unavailable' : ''} ${snapshot.isDragging ? 'dragging' : ''}`} 
                                  data-testid={`menu-item-card-${item.id}`}
                                >
                                  {canReorder && (
                                    <div className="drag-handle-card" {...provided.dragHandleProps}>
                                      <GripVertical size={18} />
                                    </div>
                                  )}
                                  {item.image_url && (
                                    <div className="item-card-image">
                                      <img src={item.image_url} alt={getTranslation(item.translations, primaryLang, 'name')} />
                                    </div>
                                  )}
                                  <div className="item-card-content">
                                    <div className="item-card-header">
                                      <h3 className="item-card-title">{getTranslation(item.translations, primaryLang, 'name')}</h3>
                                      <span className={`status-dot ${item.available !== false ? 'available' : 'unavailable'}`} />
                                    </div>
                                    <p className="item-card-category">
                                      {categories.find(c => c.id === item.category_id) 
                                        ? getTranslation(categories.find(c => c.id === item.category_id).translations, primaryLang, 'name') 
                                        : '-'}
                                    </p>
                                    <p className="item-card-price">{formatCurrency(item.base_price)}</p>
                                    <div className="item-card-actions">
                                      <button onClick={() => openDialog('item', item)} className="edit-btn">
                                        <Edit2 size={16} />
                                      </button>
                                      <button onClick={() => handleDelete(item.id, 'item', item)} className="delete-btn">
                                        <Trash2 size={16} />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {droppableProvided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                );
              }
              
              // List view (default)
              return (
                <DragDropContext onDragEnd={(result) => canReorder && handleItemDragEnd(result, filteredItems)}>
                  <Droppable droppableId="items-list">
                    {(droppableProvided) => (
                      <table>
                        <thead>
                          <tr>
                            {canReorder && <th style={{ width: '50px' }}></th>}
                            <th>{t('menu.itemName')}</th>
                            <th>{t('menu.categoryName')}</th>
                            <th>{t('menu.basePrice')}</th>
                            <th>{t('common.status')}</th>
                            <th>{t('common.actions')}</th>
                          </tr>
                        </thead>
                        <tbody ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
                          {paginatedItems.map((item, index) => (
                            <Draggable 
                              key={item.id} 
                              draggableId={item.id} 
                              index={index}
                              isDragDisabled={!canReorder}
                            >
                              {(provided, snapshot) => (
                                <tr 
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  data-testid={`menu-item-row-${item.id}`}
                                  className={snapshot.isDragging ? 'dragging-row' : ''}
                                >
                                  {canReorder && (
                                    <td>
                                      <div className="drag-handle" {...provided.dragHandleProps}>
                                        <GripVertical size={18} />
                                      </div>
                                    </td>
                                  )}
                                  <td data-testid="item-name">{getTranslation(item.translations, primaryLang, 'name')}</td>
                                  <td>{categories.find(c => c.id === item.category_id) ? getTranslation(categories.find(c => c.id === item.category_id).translations, primaryLang, 'name') : '-'}</td>
                                  <td data-testid="item-price">{formatCurrency(item.base_price)}</td>
                                  <td data-testid="item-status">
                                    <span className={`status-badge ${item.available !== false ? 'available' : 'unavailable'}`}>
                                      {item.available !== false ? 'Available' : 'Unavailable'}
                                    </span>
                                  </td>
                                  <td>
                                    <div className="flex gap-2">
                                      <button onClick={() => openDialog('item', item)} className="edit-btn" data-testid="edit-item-btn">
                                        <Edit2 size={16} />
                                      </button>
                                      <button onClick={() => handleDelete(item.id, 'item', item)} className="delete-btn" data-testid="delete-item-btn">
                                        <Trash2 size={16} />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </Draggable>
                          ))}
                          {droppableProvided.placeholder}
                        </tbody>
                      </table>
                    )}
                  </Droppable>
                </DragDropContext>
              );
            })()}
            
            {/* Pagination Controls */}
            {(() => {
              const primaryLang = tenant?.supportedLanguages?.[0] || 'tr';
              const filteredItemsCount = menuItems.filter(item => {
                const categoryMatch = filterCategory === 'all' || item.category_id === filterCategory;
                const availabilityMatch = 
                  filterAvailability === 'all' || 
                  (filterAvailability === 'available' && item.available !== false) ||
                  (filterAvailability === 'unavailable' && item.available === false);
                const nameMatch = !searchTerm || 
                  getTranslation(item.translations, primaryLang, 'name')?.toLowerCase().includes(searchTerm.toLowerCase());
                return categoryMatch && availabilityMatch && nameMatch;
              }).length;
              
              const totalPages = Math.ceil(filteredItemsCount / itemsPerPage);
              const startItem = filteredItemsCount === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
              const endItem = Math.min(currentPage * itemsPerPage, filteredItemsCount);
              
              // Generate page numbers with ellipsis
              const getPageNumbers = () => {
                const pages = [];
                const showEllipsisStart = currentPage > 3;
                const showEllipsisEnd = currentPage < totalPages - 2;
                
                if (totalPages <= 7) {
                  // Show all pages if 7 or less
                  for (let i = 1; i <= totalPages; i++) pages.push(i);
                } else {
                  // Always show first page
                  pages.push(1);
                  
                  if (showEllipsisStart) pages.push('...');
                  
                  // Show pages around current
                  const start = showEllipsisStart ? Math.max(2, currentPage - 1) : 2;
                  const end = showEllipsisEnd ? Math.min(totalPages - 1, currentPage + 1) : totalPages - 1;
                  
                  for (let i = start; i <= end; i++) {
                    if (!pages.includes(i)) pages.push(i);
                  }
                  
                  if (showEllipsisEnd) pages.push('...');
                  
                  // Always show last page
                  if (!pages.includes(totalPages)) pages.push(totalPages);
                }
                
                return pages;
              };
              
              if (filteredItemsCount === 0) return null;
              
              return (
                <div className="pagination-container">
                  <div className="pagination-info-left">
                    <span>Showing {startItem}-{endItem} of {filteredItemsCount}</span>
                    <select
                      value={itemsPerPage}
                      onChange={(e) => {
                        setItemsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                      className="items-per-page-select"
                    >
                      <option value={10}>10 / page</option>
                      <option value={25}>25 / page</option>
                      <option value={50}>50 / page</option>
                      <option value={100}>100 / page</option>
                    </select>
                  </div>
                  
                  {totalPages > 1 && (
                    <div className="pagination-controls">
                      <button
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                        className="pagination-btn pagination-btn-icon"
                        title="First page"
                      >
                        ««
                      </button>
                      <button
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="pagination-btn pagination-btn-icon"
                        title="Previous page"
                      >
                        ‹
                      </button>
                      
                      <div className="pagination-pages">
                        {getPageNumbers().map((page, idx) => (
                          page === '...' ? (
                            <span key={`ellipsis-${idx}`} className="pagination-ellipsis">…</span>
                          ) : (
                            <button
                              key={page}
                              onClick={() => setCurrentPage(page)}
                              className={`pagination-btn pagination-btn-page ${currentPage === page ? 'active' : ''}`}
                            >
                              {page}
                            </button>
                          )
                        ))}
                      </div>
                      
                      <button
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className="pagination-btn pagination-btn-icon"
                        title="Next page"
                      >
                        ›
                      </button>
                      <button
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages}
                        className="pagination-btn pagination-btn-icon"
                        title="Last page"
                      >
                        »»
                      </button>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </TabsContent>

        <TabsContent value="modifiers">
          <div className="section-header">
            <h2 className="section-title">{t('menu.modifierGroups') || 'Modifier Groups'}</h2>
            <Button onClick={() => openDialog('modifier')} data-testid="add-modifier-btn">
              <Plus size={16} /> {t('menu.addModifierGroup') || 'Add Modifier Group'}
            </Button>
          </div>

          <div className="card mt-4">
            {(() => {
              const primaryLang = tenant?.supportedLanguages?.[0] || 'tr';
              const selectionTypeLabels = {
                single: t('menu.selectionSingle') || 'Single Selection',
                multiple: t('menu.selectionMultiple') || 'Multiple Selection'
              };
              
              if (viewMode === 'grid') {
                return (
                  <div className="items-grid">
                    {modifierGroups.map((group) => (
                      <div key={group.id} className="modifier-group-card" data-testid={`modifier-group-card-${group.id}`}>
                        <div className="modifier-group-card-header">
                          <h3 className="modifier-group-card-title">
                            {getTranslation(group.translations, primaryLang, 'name')}
                          </h3>
                          <span className={`selection-badge ${group.selection_type}`}>
                            {selectionTypeLabels[group.selection_type]}
                          </span>
                        </div>
                        <div className="modifier-group-rules">
                          {group.min_selections > 0 ? (
                            <span className="required-badge">{t('menu.required') || 'Required'}</span>
                          ) : (
                            <span className="optional-badge">{t('menu.optional') || 'Optional'}</span>
                          )}
                          <span className="selection-range">
                            {group.min_selections === group.max_selections 
                              ? `${t('menu.selectExactly') || 'Select exactly'} ${group.min_selections}`
                              : `${t('menu.select') || 'Select'} ${group.min_selections} - ${group.max_selections}`}
                          </span>
                        </div>
                        <div className="modifier-options-list">
                          {(group.options || []).slice(0, 3).map((opt, idx) => (
                            <div key={opt.id || idx} className="modifier-option-preview">
                              <span>{getTranslation(opt.translations, primaryLang, 'name')}</span>
                              {opt.price_adjustment !== 0 && (
                                <span className="option-price">
                                  {opt.price_adjustment > 0 ? '+' : ''}{formatCurrency(opt.price_adjustment)}
                                </span>
                              )}
                            </div>
                          ))}
                          {(group.options || []).length > 3 && (
                            <div className="more-options">
                              +{(group.options || []).length - 3} {t('menu.moreOptions') || 'more options'}
                            </div>
                          )}
                        </div>
                        <div className="modifier-group-card-actions">
                          <button onClick={() => openDialog('modifier', group)} className="edit-btn">
                            <Edit2 size={16} />
                          </button>
                          <button onClick={() => handleDelete(group.id, 'modifier', group)} className="delete-btn">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              }
              
              // List view - expanded accordion style for modifier groups
              return (
                <div className="modifier-groups-list">
                  {modifierGroups.map((group) => (
                    <div key={group.id} className="modifier-group-item" data-testid={`modifier-group-${group.id}`}>
                      <div className="modifier-group-header">
                        <div className="modifier-group-info">
                          <h3 className="modifier-group-name">
                            {getTranslation(group.translations, primaryLang, 'name')}
                          </h3>
                          <div className="modifier-group-meta">
                            <span className={`selection-badge ${group.selection_type}`}>
                              {selectionTypeLabels[group.selection_type]}
                            </span>
                            {group.min_selections > 0 ? (
                              <span className="required-badge">{t('menu.required') || 'Required'}</span>
                            ) : (
                              <span className="optional-badge">{t('menu.optional') || 'Optional'}</span>
                            )}
                            <span className="options-count">
                              {(group.options || []).length} {t('menu.options') || 'options'}
                            </span>
                          </div>
                        </div>
                        <div className="modifier-group-actions">
                          <button onClick={() => openDialog('modifier', group)} className="edit-btn" data-testid="edit-modifier-btn">
                            <Edit2 size={16} />
                          </button>
                          <button onClick={() => handleDelete(group.id, 'modifier', group)} className="delete-btn" data-testid="delete-modifier-btn">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                      <div className="modifier-options-table">
                        <table>
                          <thead>
                            <tr>
                              <th>{t('menu.optionName') || 'Option Name'}</th>
                              <th>{t('menu.priceAdjustment') || 'Price Adjustment'}</th>
                              <th>{t('menu.available') || 'Available'}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(group.options || []).map((opt, idx) => (
                              <tr key={opt.id || idx}>
                                <td>{getTranslation(opt.translations, primaryLang, 'name')}</td>
                                <td className={opt.price_adjustment > 0 ? 'positive-price' : opt.price_adjustment < 0 ? 'negative-price' : ''}>
                                  {opt.price_adjustment !== 0 && (opt.price_adjustment > 0 ? '+' : '')}
                                  {formatCurrency(opt.price_adjustment)}
                                </td>
                                <td>
                                  <span className={`availability-badge ${opt.available !== false ? 'available' : 'unavailable'}`}>
                                    {opt.available !== false ? (t('common.yes') || 'Yes') : (t('common.no') || 'No')}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        </TabsContent>
      </Tabs>

      {/* Add Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="menu-dialog" aria-describedby="menu-dialog-description" className={currentForm === 'modifier' ? 'max-w-2xl' : ''}>
          <DialogHeader>
            <DialogTitle>
              {editMode ? t('actions.edit') : t('actions.add')} {' '}
              {currentForm === 'category' && (editMode ? 'Category' : t('menu.addCategory'))}
              {currentForm === 'item' && (editMode ? 'Menu Item' : t('menu.addMenuItem'))}
              {currentForm === 'modifier' && (editMode ? (t('menu.modifierGroup') || 'Modifier Group') : (t('menu.addModifierGroup') || 'Add Modifier Group'))}
            </DialogTitle>
            <p id="menu-dialog-description" className="text-sm text-gray-500">
              {editMode ? `Update the ${currentForm === 'modifier' ? 'modifier group' : currentForm} details below.` : `Fill in the form below to add a new ${currentForm === 'modifier' ? 'modifier group' : currentForm}.`}
            </p>
          </DialogHeader>

          <form onSubmit={(e) => handleSubmit(e, currentForm)} className="form">
            {currentForm === 'category' && (
              <>
                {(tenant?.supportedLanguages || ['tr']).map(lang => (
                  <div className="form-group" key={`category-name-${lang}`}>
                    <label>
                      {t('menu.categoryName')}
                      {(tenant?.supportedLanguages || ['tr']).length > 1 && ` (${LANGUAGE_NAMES[lang] || lang.toUpperCase()})`}
                    </label>
                    <input
                      type="text"
                      value={getTranslation(formData.translations, lang, 'name')}
                      onChange={(e) => updateTranslation(lang, 'name', e.target.value)}
                      required={lang === (tenant?.supportedLanguages?.[0] || 'tr')}
                      data-testid={`category-name-${lang}-input`}
                    />
                  </div>
                ))}
                <div className="form-group">
                  <label>VAT Percentage (%) - Optional</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    placeholder="Leave empty to use global VAT"
                    value={formData.vat_percentage || ''}
                    onChange={(e) => setFormData({ ...formData, vat_percentage: e.target.value ? parseFloat(e.target.value) : null })}
                    data-testid="category-vat-input"
                  />
                  <small className="text-gray-500">Override global VAT for this category</small>
                </div>
                <div className="form-group">
                  <label>{t('menu.parentCategory')} ({t('common.optional')})</label>
                  <select
                    value={formData.parent_category_id || ''}
                    onChange={(e) => setFormData({ ...formData, parent_category_id: e.target.value || null })}
                    data-testid="category-parent-select"
                    disabled={formData.is_parent && editMode}
                  >
                    <option value="">{t('menu.noParent')}</option>
                    {categories
                      .filter(cat => 
                        !cat.parent_category_id && // Only root categories can be parents
                        cat.id !== formData.id // Can't be parent of itself
                      )
                      .map(cat => (
                        <option key={cat.id} value={cat.id}>
                          {getTranslation(cat.translations, 'en', 'name')}
                        </option>
                      ))
                    }
                  </select>
                  <small className="text-gray-500">
                    {formData.is_parent && editMode 
                      ? t('menu.parentHasChildren')
                      : t('menu.parentCategoryHint')}
                  </small>
                </div>
              </>
            )}

            {currentForm === 'modifier' && (
              <>
                {/* Group Name */}
                {(tenant?.supportedLanguages || ['tr']).map(lang => (
                  <div className="form-group" key={`modifier-group-name-${lang}`}>
                    <label>
                      {t('menu.groupName') || 'Group Name'}
                      {(tenant?.supportedLanguages || ['tr']).length > 1 && ` (${LANGUAGE_NAMES[lang] || lang.toUpperCase()})`}
                    </label>
                    <input
                      type="text"
                      value={getTranslation(formData.translations, lang, 'name')}
                      onChange={(e) => updateTranslation(lang, 'name', e.target.value)}
                      required={lang === (tenant?.supportedLanguages?.[0] || 'tr')}
                      placeholder={t('menu.groupNamePlaceholder') || 'e.g., Size, Toppings, Cooking Level'}
                      data-testid={`modifier-group-name-${lang}-input`}
                    />
                  </div>
                ))}
                
                {/* Selection Type */}
                <div className="form-group">
                  <label>{t('menu.selectionType') || 'Selection Type'}</label>
                  <select
                    value={formData.selection_type || 'single'}
                    onChange={(e) => setFormData({ ...formData, selection_type: e.target.value })}
                    data-testid="modifier-selection-type"
                  >
                    <option value="single">{t('menu.selectionSingle') || 'Single Selection'} - {t('menu.selectOnlyOne') || 'Customer can select only one option'}</option>
                    <option value="multiple">{t('menu.selectionMultiple') || 'Multiple Selection'} - {t('menu.selectMultiple') || 'Customer can select multiple options'}</option>
                  </select>
                </div>
                
                {/* Selection Rules */}
                <div className="form-group-row">
                  <div className="form-group">
                    <label>{t('menu.minSelections') || 'Minimum Selections'}</label>
                    <input
                      type="number"
                      min="0"
                      value={formData.min_selections ?? 0}
                      onChange={(e) => setFormData({ ...formData, min_selections: parseInt(e.target.value) || 0 })}
                      data-testid="modifier-min-selections"
                    />
                    <small className="text-gray-500">{t('menu.minSelectionsHelp') || 'Set to 0 for optional, 1+ for required'}</small>
                  </div>
                  <div className="form-group">
                    <label>{t('menu.maxSelections') || 'Maximum Selections'}</label>
                    <input
                      type="number"
                      min="1"
                      value={formData.max_selections ?? 1}
                      onChange={(e) => setFormData({ ...formData, max_selections: parseInt(e.target.value) || 1 })}
                      data-testid="modifier-max-selections"
                    />
                    <small className="text-gray-500">{t('menu.maxSelectionsHelp') || 'How many options can be selected'}</small>
                  </div>
                </div>
                
                {/* Options */}
                <div className="form-group">
                  <div className="options-header">
                    <label>{t('menu.options') || 'Options'}</label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const newOption = {
                          id: `opt_${Date.now()}`,
                          translations: {},
                          price_adjustment: 0,
                          available: true
                        };
                        setFormData({
                          ...formData,
                          options: [...(formData.options || []), newOption]
                        });
                      }}
                      data-testid="add-option-btn"
                    >
                      <Plus size={14} /> {t('menu.addOption') || 'Add Option'}
                    </Button>
                  </div>
                  
                  <div className="options-list">
                    {(formData.options || []).map((option, optIdx) => (
                      <div key={option.id || optIdx} className="option-item">
                        <div className="option-fields">
                          {(tenant?.supportedLanguages || ['tr']).map(lang => (
                            <input
                              key={`opt-${optIdx}-name-${lang}`}
                              type="text"
                              placeholder={`${t('menu.optionName') || 'Option name'}${(tenant?.supportedLanguages || ['tr']).length > 1 ? ` (${lang.toUpperCase()})` : ''}`}
                              value={getTranslation(option.translations, lang, 'name')}
                              onChange={(e) => {
                                const updatedOptions = [...(formData.options || [])];
                                const updatedTranslations = setTranslation(option.translations || {}, lang, 'name', e.target.value);
                                updatedOptions[optIdx] = { ...option, translations: updatedTranslations };
                                setFormData({ ...formData, options: updatedOptions });
                              }}
                              required={lang === (tenant?.supportedLanguages?.[0] || 'tr')}
                              data-testid={`option-${optIdx}-name-${lang}`}
                            />
                          ))}
                          <div className="option-price-field">
                            <span className="price-prefix">+/-</span>
                            <input
                              type="number"
                              step="0.01"
                              placeholder={t('menu.priceAdjustment') || 'Price adj.'}
                              value={option.price_adjustment ?? 0}
                              onChange={(e) => {
                                const updatedOptions = [...(formData.options || [])];
                                updatedOptions[optIdx] = { ...option, price_adjustment: parseFloat(e.target.value) || 0 };
                                setFormData({ ...formData, options: updatedOptions });
                              }}
                              data-testid={`option-${optIdx}-price`}
                            />
                          </div>
                          <label className="option-available-toggle">
                            <input
                              type="checkbox"
                              checked={option.available !== false}
                              onChange={(e) => {
                                const updatedOptions = [...(formData.options || [])];
                                updatedOptions[optIdx] = { ...option, available: e.target.checked };
                                setFormData({ ...formData, options: updatedOptions });
                              }}
                              data-testid={`option-${optIdx}-available`}
                            />
                            <span>{t('menu.available') || 'Available'}</span>
                          </label>
                        </div>
                        <button
                          type="button"
                          className="delete-btn"
                          onClick={() => {
                            const updatedOptions = (formData.options || []).filter((_, i) => i !== optIdx);
                            setFormData({ ...formData, options: updatedOptions });
                          }}
                          data-testid={`delete-option-${optIdx}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                    {(!formData.options || formData.options.length === 0) && (
                      <p className="no-options-message">{t('menu.noOptions') || 'No options added yet. Click "Add Option" to create options for this modifier group.'}</p>
                    )}
                  </div>
                </div>
              </>
            )}

            {currentForm === 'item' && (
              <>
                {(tenant?.supportedLanguages || ['tr']).map(lang => (
                  <div className="form-group" key={`item-name-${lang}`}>
                    <label>
                      {t('menu.itemName')}
                      {(tenant?.supportedLanguages || ['tr']).length > 1 && ` (${LANGUAGE_NAMES[lang] || lang.toUpperCase()})`}
                    </label>
                    <input
                      type="text"
                      value={getTranslation(formData.translations, lang, 'name')}
                      onChange={(e) => updateTranslation(lang, 'name', e.target.value)}
                      required={lang === (tenant?.supportedLanguages?.[0] || 'tr')}
                      data-testid={`item-name-${lang}-input`}
                    />
                  </div>
                ))}
                <div className="form-group">
                  <label>{t('menu.categoryName')}</label>
                  <select
                    value={formData.category_id || ''}
                    onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                    required
                    data-testid="item-category-select"
                  >
                    <option value="">{t('common.select')}...</option>
                    {categories
                      .filter(cat => !cat.is_parent) // Only show leaf categories (no subcategories)
                      .map(cat => {
                        const parentCat = categories.find(p => p.id === cat.parent_category_id);
                        const primaryLang = tenant?.supportedLanguages?.[0] || 'tr';
                        const displayName = parentCat 
                          ? `${getTranslation(parentCat.translations, primaryLang, 'name')} → ${getTranslation(cat.translations, primaryLang, 'name')}`
                          : getTranslation(cat.translations, primaryLang, 'name');
                        return (
                          <option key={cat.id} value={cat.id}>{displayName}</option>
                        );
                      })}
                  </select>
                  <small className="text-gray-500">{t('menu.onlyLeafCategories')}</small>
                </div>
                <div className="form-group">
                  <label>{t('menu.basePrice')}</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.base_price || ''}
                    onChange={(e) => setFormData({ ...formData, base_price: parseFloat(e.target.value) })}
                    required
                    data-testid="item-price-input"
                  />
                </div>
                {/* Image URL - only available for PRO/Enterprise plans */}
                {tenant?.plan && tenant.plan.toUpperCase() !== 'STARTER' && tenant.plan.toLowerCase() !== 'free' && (
                  <div className="form-group">
                    <label>Image URL (Optional)</label>
                    <input
                      type="url"
                      placeholder="https://example.com/image.jpg"
                      value={formData.image_url || ''}
                      onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                      data-testid="item-image-url-input"
                    />
                    <small className="text-gray-500">Add an image URL for the public menu</small>
                  </div>
                )}
                {(tenant?.supportedLanguages || ['tr']).map(lang => (
                  <div className="form-group" key={`item-desc-${lang}`}>
                    <label>
                      {t('menu.description')}
                      {(tenant?.supportedLanguages || ['tr']).length > 1 && ` (${LANGUAGE_NAMES[lang] || lang.toUpperCase()})`}
                    </label>
                    <textarea
                      rows="2"
                      value={getTranslation(formData.translations, lang, 'description')}
                      onChange={(e) => updateTranslation(lang, 'description', e.target.value)}
                      data-testid={`item-description-${lang}-input`}
                    />
                  </div>
                ))}
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.available !== false}
                      onChange={(e) => setFormData({ ...formData, available: e.target.checked })}
                      data-testid="item-available-checkbox"
                    />
                    <span>{t('menu.available')}</span>
                  </label>
                </div>
                
                {/* Modifier Groups Selection */}
                {modifierGroups.length > 0 && (
                  <div className="form-group">
                    <label>{t('menu.modifierGroups') || 'Modifier Groups'}</label>
                    <div className="modifier-groups-selection">
                      {modifierGroups.map((group) => {
                        const primaryLang = tenant?.supportedLanguages?.[0] || 'tr';
                        const groupName = getTranslation(group.translations, primaryLang, 'name');
                        const isSelected = (formData.modifier_group_ids || []).includes(group.id);
                        return (
                          <label key={group.id} className={`modifier-group-checkbox ${isSelected ? 'selected' : ''}`}>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => {
                                const currentIds = formData.modifier_group_ids || [];
                                const newIds = e.target.checked
                                  ? [...currentIds, group.id]
                                  : currentIds.filter(id => id !== group.id);
                                setFormData({ ...formData, modifier_group_ids: newIds });
                              }}
                              data-testid={`modifier-group-${group.id}-checkbox`}
                            />
                            <div className="modifier-group-info">
                              <span className="modifier-group-name">{groupName}</span>
                              <span className="modifier-group-meta">
                                {group.selection_type === 'single' ? (t('menu.selectionSingle') || 'Single') : (t('menu.selectionMultiple') || 'Multiple')}
                                {' • '}
                                {(group.options || []).length} {t('menu.options') || 'options'}
                                {group.min_selections > 0 && (
                                  <span className="required-indicator"> • {t('menu.required') || 'Required'}</span>
                                )}
                              </span>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                    <small className="text-gray-500">{t('menu.modifierGroupsHelp') || 'Select modifier groups that apply to this item (e.g., Size, Toppings)'}</small>
                  </div>
                )}
              </>
            )}

            <Button type="submit" className="w-full" data-testid="save-btn">{t('actions.save')}</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent data-testid="delete-dialog">
          <DialogHeader>
            <DialogTitle>Confirm Deletion</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-700 mb-3">
              Are you sure you want to delete this {itemToDelete?.type === 'modifier' ? 'modifier group' : itemToDelete?.type}?
            </p>
            {itemToDelete && (
              <div className="bg-gray-50 p-3 rounded border border-gray-200">
                <p className="font-medium text-gray-900">
                  {itemToDelete.type === 'category' && getTranslation(itemToDelete.item.translations, 'en', 'name')}
                  {itemToDelete.type === 'item' && getTranslation(itemToDelete.item.translations, 'en', 'name')}
                  {itemToDelete.type === 'modifier' && getTranslation(itemToDelete.item.translations, 'en', 'name')}
                </p>
                {itemToDelete.type === 'item' && (
                  <p className="text-sm text-gray-600 mt-1">
                    Price: {formatCurrency(itemToDelete.item.base_price)}
                  </p>
                )}
                {itemToDelete.type === 'modifier' && (
                  <p className="text-sm text-gray-600 mt-1">
                    {(itemToDelete.item.options || []).length} {t('menu.options') || 'options'}
                  </p>
                )}
              </div>
            )}
            <p className="text-sm text-red-600 mt-3 font-medium">
              This action cannot be undone.
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setDeleteDialogOpen(false);
                setItemToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              data-testid="confirm-delete-btn"
            >
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <style>{`
        .menu-management {
          width: 100%;
        }

        .tabs-header-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
          flex-wrap: wrap;
          gap: 16px;
        }

        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .section-header-actions {
          display: flex;
          gap: 8px;
          align-items: center;
          flex-wrap: wrap;
        }

        .import-export-locked {
          opacity: 0.6;
          border-style: dashed !important;
        }
        .import-export-locked:hover {
          opacity: 1;
        }

        .mt-4 {
          margin-top: 16px;
        }

        .filter-controls {
          display: flex;
          gap: 12px;
          align-items: center;
        }

        .filter-select {
          padding: 8px 12px;
          border: 1px solid hsl(var(--border));
          border-radius: 6px;
          background: hsl(var(--card));
          color: hsl(var(--foreground));
          font-size: 14px;
          min-width: 140px;
        }

        .filter-select:focus {
          outline: none;
          border-color: hsl(var(--primary));
          box-shadow: 0 0 0 3px hsl(var(--primary) / 0.1);
        }

        .status-badge {
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 500;
        }

        .status-badge.available {
          background: #dcfce7;
          color: #166534;
        }

        .status-badge.unavailable {
          background: #fee2e2;
          color: #dc2626;
        }

        .section-title {
          font-size: 20px;
          font-weight: 600;
        }

        .delete-btn {
          padding: 6px;
          background: #fee2e2;
          color: #dc2626;
          border-radius: 6px;
        }

        .delete-btn:hover {
          background: #fecaca;
        }

        .edit-btn {
          padding: 6px;
          background: #dbeafe;
          color: #2563eb;
          border-radius: 6px;
        }

        .edit-btn:hover {
          background: #bfdbfe;
        }

        .order-btn {
          padding: 4px;
          background: hsl(var(--muted));
          color: hsl(var(--muted-foreground));
          border-radius: 4px;
          border: 1px solid hsl(var(--border));
          cursor: pointer;
        }

        .order-btn:hover:not(:disabled) {
          background: hsl(var(--muted) / 0.8);
          color: hsl(var(--foreground));
        }

        .order-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .gap-1 {
          gap: 4px;
        }

        .flex {
          display: flex;
        }

        .gap-2 {
          gap: 8px;
        }

        .form {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .form-group label {
          font-size: 13px;
          font-weight: 600;
          color: hsl(var(--foreground));
        }

        .checkbox-label {
          display: flex !important;
          flex-direction: row !important;
          align-items: center;
          gap: 8px;
          cursor: pointer;
        }

        .checkbox-label input[type="checkbox"] {
          margin: 0;
          cursor: pointer;
        }

        .checkbox-label span {
          font-size: 14px;
          font-weight: 500;
          color: hsl(var(--foreground));
        }

        .pagination-container {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px;
          border-top: 1px solid hsl(var(--border));
          flex-wrap: wrap;
          gap: 16px;
        }

        .pagination-info-left {
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 14px;
          color: hsl(var(--muted-foreground));
        }

        .items-per-page-select {
          padding: 6px 10px;
          border: 1px solid hsl(var(--border));
          border-radius: 6px;
          background: hsl(var(--card));
          font-size: 13px;
          color: hsl(var(--foreground));
          cursor: pointer;
        }

        .items-per-page-select:focus {
          outline: none;
          border-color: hsl(var(--primary));
          box-shadow: 0 0 0 2px hsl(var(--primary) / 0.1);
        }

        .pagination-controls {
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .pagination-pages {
          display: flex;
          align-items: center;
          gap: 4px;
          margin: 0 4px;
        }

        .pagination-btn {
          padding: 8px 12px;
          background: hsl(var(--card));
          color: hsl(var(--foreground));
          border: 1px solid hsl(var(--border));
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s;
          min-width: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .pagination-btn:hover:not(:disabled):not(.active) {
          background: hsl(var(--muted));
          border-color: hsl(var(--border));
        }

        .pagination-btn:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }

        .pagination-btn.active {
          background: #3b82f6;
          color: white;
          border-color: #3b82f6;
        }

        .pagination-btn-icon {
          padding: 8px 10px;
          font-size: 16px;
        }

        .pagination-btn-page {
          min-width: 40px;
        }

        .pagination-ellipsis {
          padding: 8px 4px;
          color: #9ca3af;
          font-size: 14px;
        }

        @media (max-width: 640px) {
          .pagination-container {
            flex-direction: column;
            align-items: stretch;
          }
          
          .pagination-controls {
            justify-content: center;
          }
          
          .pagination-info-left {
            justify-content: center;
          }
        }

        /* View Toggle Styles */
        .view-toggle {
          display: flex;
          background: hsl(var(--muted));
          border-radius: 8px;
          padding: 4px;
          gap: 4px;
        }

        .view-toggle-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 8px 12px;
          background: transparent;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          color: hsl(var(--muted-foreground));
          transition: all 0.15s;
        }

        .view-toggle-btn:hover {
          color: hsl(var(--foreground));
          background: hsl(var(--muted) / 0.5);
        }

        .view-toggle-btn.active {
          background: hsl(var(--card));
          color: hsl(var(--primary));
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }

        /* Grid View Styles */
        .items-grid {
          display: flex;
          flex-wrap: wrap;
          gap: 16px;
          padding: 20px;
        }

        .item-card {
          background: hsl(var(--card));
          border: 1px solid hsl(var(--border));
          border-radius: 12px;
          overflow: hidden;
          transition: all 0.2s;
          width: calc(33.333% - 11px);
          min-width: 280px;
          flex-grow: 0;
          flex-shrink: 0;
        }

        .item-card.can-drag {
          cursor: grab;
        }

        .item-card.can-drag:active {
          cursor: grabbing;
        }

        .item-card:hover {
          box-shadow: 0 4px 12px rgba(0,0,0,0.08);
          border-color: #d1d5db;
        }

        .item-card.unavailable {
          opacity: 0.7;
        }

        .item-card-image {
          width: 100%;
          height: 140px;
          background: hsl(var(--muted));
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        }

        .item-card-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .item-card-image svg {
          color: hsl(var(--muted-foreground));
        }

        .item-card-content {
          padding: 16px;
        }

        .item-card-header {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 4px;
        }

        .item-card-title {
          font-size: 16px;
          font-weight: 600;
          color: hsl(var(--foreground));
          flex: 1;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .status-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          flex-shrink: 0;
        }

        .status-dot.available {
          background: #10b981;
        }

        .status-dot.unavailable {
          background: #ef4444;
        }

        .item-card-category {
          font-size: 13px;
          color: hsl(var(--muted-foreground));
          margin-bottom: 8px;
        }

        .item-card-price {
          font-size: 18px;
          font-weight: 700;
          color: hsl(var(--primary));
          margin-bottom: 12px;
        }

        .item-card-actions {
          display: flex;
          align-items: center;
          gap: 8px;
          padding-top: 12px;
          border-top: 1px solid hsl(var(--border));
        }

        /* Drag and Drop Styles */
        .drag-handle {
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: grab;
          color: #9ca3af;
          padding: 4px;
          border-radius: 4px;
          transition: all 0.15s;
        }

        .drag-handle:hover {
          color: hsl(var(--foreground));
          background: hsl(var(--muted));
        }

        .drag-handle:active {
          cursor: grabbing;
        }

        .drag-handle-card {
          position: absolute;
          top: 8px;
          right: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: grab;
          color: #9ca3af;
          padding: 6px;
          border-radius: 6px;
          background: hsl(var(--card) / 0.9);
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          transition: all 0.15s;
          z-index: 10;
        }

        .drag-handle-card:hover {
          color: hsl(var(--foreground));
          background: hsl(var(--card));
          box-shadow: 0 2px 6px rgba(0,0,0,0.15);
        }

        .drag-handle-card:active {
          cursor: grabbing;
        }

        .item-card {
          position: relative;
        }

        .item-card.dragging {
          box-shadow: 0 8px 24px rgba(0,0,0,0.15);
          transform: rotate(2deg);
          z-index: 100;
          opacity: 0.9;
        }

        .dragging-row {
          background: hsl(var(--primary) / 0.1) !important;
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        }

        .dragging-row td {
          background: hsl(var(--primary) / 0.1) !important;
        }

        @media (max-width: 900px) {
          .item-card, .category-card {
            width: calc(50% - 8px);
          }
        }

        @media (max-width: 600px) {
          .item-card, .category-card {
            width: 100%;
          }
          .items-grid {
            padding: 12px;
          }
        }

        /* Category Card Styles */
        .category-card {
          background: hsl(var(--card));
          border: 1px solid hsl(var(--border));
          border-radius: 12px;
          overflow: hidden;
          transition: all 0.2s;
          width: calc(33.333% - 11px);
          min-width: 280px;
          flex-grow: 0;
          flex-shrink: 0;
          position: relative;
        }

        .category-card:hover {
          box-shadow: 0 4px 12px rgba(0,0,0,0.08);
          border-color: hsl(var(--border));
        }

        .category-card.dragging {
          box-shadow: 0 8px 24px rgba(0,0,0,0.15);
          transform: rotate(2deg);
          z-index: 100;
          opacity: 0.9;
        }

        .category-card-content {
          padding: 16px;
        }

        .category-card-header {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 8px;
        }

        .category-card-title {
          font-size: 16px;
          font-weight: 600;
          color: hsl(var(--foreground));
          flex: 1;
        }

        .category-card-subs {
          font-size: 13px;
          color: hsl(var(--muted-foreground));
          margin-bottom: 12px;
        }

        .category-card-actions {
          display: flex;
          align-items: center;
          gap: 8px;
          padding-top: 12px;
          border-top: 1px solid hsl(var(--border));
          margin-bottom: 12px;
        }

        .category-subcategories {
          display: flex;
          flex-direction: column;
          gap: 8px;
          padding-top: 12px;
          border-top: 1px solid hsl(var(--border));
        }

        .subcategory-chip {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 8px 12px;
          background: hsl(var(--muted));
          border-radius: 8px;
          font-size: 13px;
          color: hsl(var(--foreground));
        }

        .subcategory-actions {
          display: flex;
          gap: 4px;
        }

        .edit-btn-sm, .delete-btn-sm {
          padding: 4px;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.15s;
        }

        .edit-btn-sm {
          background: #e0f2fe;
          color: #0369a1;
        }

        .edit-btn-sm:hover {
          background: #bae6fd;
        }

        .delete-btn-sm {
          background: #fee2e2;
          color: #dc2626;
        }

        .delete-btn-sm:hover {
          background: #fecaca;
        }

        /* Modifier Card Styles */
        .modifier-card {
          background: hsl(var(--card));
          border: 1px solid hsl(var(--border));
          border-radius: 12px;
          overflow: hidden;
          transition: all 0.2s;
          width: calc(33.333% - 11px);
          min-width: 200px;
          flex-grow: 0;
          flex-shrink: 0;
        }

        .modifier-card:hover {
          box-shadow: 0 4px 12px rgba(0,0,0,0.08);
          border-color: hsl(var(--border));
        }

        .modifier-card-content {
          padding: 16px;
        }

        .modifier-card-title {
          font-size: 16px;
          font-weight: 600;
          color: hsl(var(--foreground));
          margin-bottom: 8px;
        }

        .modifier-card-price {
          font-size: 18px;
          font-weight: 700;
          color: hsl(var(--primary));
          margin-bottom: 12px;
        }

        .modifier-card-actions {
          display: flex;
          align-items: center;
          gap: 8px;
          padding-top: 12px;
          border-top: 1px solid hsl(var(--border));
        }

        @media (max-width: 900px) {
          .modifier-card {
            width: calc(50% - 8px);
          }
        }

        @media (max-width: 600px) {
          .modifier-card {
            width: 100%;
          }
        }

        /* Modifier Group Card Styles */
        .modifier-group-card {
          background: hsl(var(--card));
          border: 1px solid hsl(var(--border));
          border-radius: 12px;
          overflow: hidden;
          transition: all 0.2s;
          width: calc(33.333% - 11px);
          min-width: 280px;
          flex-grow: 0;
          flex-shrink: 0;
          padding: 16px;
        }

        .modifier-group-card:hover {
          box-shadow: 0 4px 12px rgba(0,0,0,0.08);
          border-color: hsl(var(--border));
        }

        .modifier-group-card-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 12px;
          margin-bottom: 12px;
        }

        .modifier-group-card-title {
          font-size: 16px;
          font-weight: 600;
          color: hsl(var(--foreground));
          margin: 0;
        }

        .selection-badge {
          font-size: 11px;
          font-weight: 500;
          padding: 4px 8px;
          border-radius: 12px;
          white-space: nowrap;
        }

        .selection-badge.single {
          background: #dbeafe;
          color: #1e40af;
        }

        .selection-badge.multiple {
          background: #f3e8ff;
          color: #7c3aed;
        }

        .modifier-group-rules {
          display: flex;
          gap: 8px;
          align-items: center;
          flex-wrap: wrap;
          margin-bottom: 12px;
        }

        .required-badge {
          font-size: 11px;
          font-weight: 500;
          padding: 3px 8px;
          border-radius: 12px;
          background: #fee2e2;
          color: #dc2626;
        }

        .optional-badge {
          font-size: 11px;
          font-weight: 500;
          padding: 3px 8px;
          border-radius: 12px;
          background: #dcfce7;
          color: #166534;
        }

        .selection-range {
          font-size: 12px;
          color: hsl(var(--muted-foreground));
        }

        .modifier-options-list {
          display: flex;
          flex-direction: column;
          gap: 6px;
          margin-bottom: 12px;
        }

        .modifier-option-preview {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 6px 10px;
          background: hsl(var(--muted));
          border-radius: 6px;
          font-size: 13px;
          color: hsl(var(--foreground));
        }

        .option-price {
          font-weight: 500;
          color: #10b981;
        }

        .more-options {
          font-size: 12px;
          color: hsl(var(--muted-foreground));
          font-style: italic;
          padding: 4px 10px;
        }

        .modifier-group-card-actions {
          display: flex;
          align-items: center;
          gap: 8px;
          padding-top: 12px;
          border-top: 1px solid hsl(var(--border));
        }

        /* Modifier Groups List View Styles */
        .modifier-groups-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
          padding: 20px;
        }

        .modifier-group-item {
          background: hsl(var(--card));
          border: 1px solid hsl(var(--border));
          border-radius: 12px;
          overflow: hidden;
        }

        .modifier-group-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px;
          background: hsl(var(--muted));
          border-bottom: 1px solid hsl(var(--border));
        }

        .modifier-group-info {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .modifier-group-name {
          font-size: 16px;
          font-weight: 600;
          color: hsl(var(--foreground));
          margin: 0;
        }

        .modifier-group-meta {
          display: flex;
          gap: 8px;
          align-items: center;
          flex-wrap: wrap;
        }

        .options-count {
          font-size: 12px;
          color: hsl(var(--muted-foreground));
        }

        .modifier-group-actions {
          display: flex;
          gap: 8px;
        }

        .modifier-options-table {
          padding: 16px;
        }

        .modifier-options-table table {
          width: 100%;
          margin: 0;
        }

        .modifier-options-table th,
        .modifier-options-table td {
          padding: 10px 12px;
          text-align: left;
        }

        .modifier-options-table th {
          background: hsl(var(--muted));
          font-weight: 600;
          color: hsl(var(--foreground));
          font-size: 13px;
        }

        .positive-price {
          color: #10b981;
          font-weight: 500;
        }

        .negative-price {
          color: #ef4444;
          font-weight: 500;
        }

        .availability-badge {
          font-size: 12px;
          font-weight: 500;
          padding: 3px 8px;
          border-radius: 12px;
        }

        .availability-badge.available {
          background: #dcfce7;
          color: #166534;
        }

        .availability-badge.unavailable {
          background: #fee2e2;
          color: #dc2626;
        }

        /* Form Styles for Modifier Groups */
        .form-group-row {
          display: flex;
          gap: 16px;
        }

        .form-group-row .form-group {
          flex: 1;
        }

        .options-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }

        .options-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
          padding: 12px;
          background: hsl(var(--muted));
          border-radius: 8px;
          border: 1px solid hsl(var(--border));
        }

        .option-item {
          display: flex;
          gap: 12px;
          align-items: flex-start;
          padding: 12px;
          background: hsl(var(--card));
          border-radius: 8px;
          border: 1px solid hsl(var(--border));
        }

        .option-fields {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .option-fields input[type="text"] {
          flex: 1;
          padding: 8px 12px;
          border: 1px solid hsl(var(--border));
          border-radius: 6px;
          font-size: 14px;
          background: hsl(var(--background));
          color: hsl(var(--foreground));
        }

        .option-price-field {
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .price-prefix {
          font-size: 12px;
          color: hsl(var(--muted-foreground));
          font-weight: 500;
        }

        .option-price-field input {
          width: 100px;
          padding: 8px 12px;
          border: 1px solid hsl(var(--border));
          border-radius: 6px;
          font-size: 14px;
        }

        .option-available-toggle {
          display: flex !important;
          flex-direction: row !important;
          align-items: center;
          gap: 6px;
          font-size: 13px;
          color: hsl(var(--foreground));
          cursor: pointer;
        }

        .no-options-message {
          text-align: center;
          color: hsl(var(--muted-foreground));
          font-size: 14px;
          padding: 20px;
        }

        /* Modifier Groups Selection in Menu Item Form */
        .modifier-groups-selection {
          display: flex;
          flex-direction: column;
          gap: 8px;
          max-height: 200px;
          overflow-y: auto;
          padding: 12px;
          background: hsl(var(--muted));
          border-radius: 8px;
          border: 1px solid hsl(var(--border));
        }

        .modifier-group-checkbox {
          display: flex !important;
          align-items: center;
          gap: 10px;
          padding: 10px 12px;
          background: hsl(var(--card));
          border: 1px solid hsl(var(--border));
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.15s;
        }

        .modifier-group-checkbox:hover {
          border-color: hsl(var(--border));
          background: hsl(var(--muted));
        }

        .modifier-group-checkbox.selected {
          border-color: hsl(var(--primary));
          background: hsl(var(--primary) / 0.1);
        }

        .modifier-group-checkbox input[type="checkbox"] {
          width: 18px;
          height: 18px;
          cursor: pointer;
        }

        .modifier-group-info {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .modifier-group-checkbox .modifier-group-name {
          font-size: 14px;
          font-weight: 500;
          color: hsl(var(--foreground));
        }

        .modifier-group-checkbox .modifier-group-meta {
          font-size: 12px;
          color: hsl(var(--muted-foreground));
        }

        .required-indicator {
          color: #dc2626;
          font-weight: 500;
        }

        @media (max-width: 900px) {
          .modifier-group-card {
            width: calc(50% - 8px);
          }
          .form-group-row {
            flex-direction: column;
          }
        }

        @media (max-width: 600px) {
          .modifier-group-card {
            width: 100%;
          }
          .option-item {
            flex-direction: column;
          }
          .option-fields {
            width: 100%;
          }
        }
      `}</style>

      {/* Import File Dialog */}
      <ImportFileDialog
        isOpen={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        importType={importType}
        supportedLanguages={tenant?.supportedLanguages}
        onSuccess={() => {
          setImportDialogOpen(false);
          fetchData();
        }}
      />
    </div>
  );
};

export default MenuManagement;
